import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

// Constants for header dimensions - must match Navbar.tsx Tailwind classes
export const HEADER_CONSTANTS = {
  ANNOUNCEMENT_HEIGHT: 40, // top-10 = 2.5rem = 40px
  NAVBAR_HEIGHT_MOBILE: 64, // h-16 = 4rem = 64px
  NAVBAR_HEIGHT_DESKTOP: 72, // lg:h-[72px] = 72px
} as const;

interface HeaderHeight {
  mobile: number;
  desktop: number;
}

interface HeaderContextType {
  /** Whether the announcement bar is currently visible */
  showAnnouncement: boolean;
  /** Function to update announcement visibility. Pass the announcement's current message
   *  when dismissing so a *different* future message (the admin editing it) isn't
   *  suppressed by an old dismissal. */
  setShowAnnouncement: (show: boolean, message?: string) => void;
  /** The message text that was last dismissed, or null if none/cleared -- compare against
   *  the live fetched announcement's message to decide whether a dismissal still applies. */
  dismissedMessage: string | null;
  /** Whether the current user is an admin */
  isAdmin: boolean;
  /** Function to update admin status (called by Navbar on auth changes) */
  setIsAdmin: (admin: boolean) => void;
  /** Calculated header height in pixels based on current state */
  headerHeight: HeaderHeight;
  /** Whether announcement should be displayed (considers both showAnnouncement and isAdmin) */
  isAnnouncementVisible: boolean;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

/**
 * Custom hook to access header context
 * @throws Error if used outside of HeaderProvider
 */
export const useHeader = (): HeaderContextType => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

interface HeaderProviderProps {
  children: ReactNode;
}

/**
 * HeaderProvider manages the global header state including:
 * - Announcement bar visibility
 * - Admin status
 * - Calculated header heights for positioning fixed elements
 */
export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  const [dismissedMessage, setDismissedMessage] = useState<string | null>(() =>
    localStorage.getItem('announcementDismissedMessage')
  );
  // showAnnouncement stays true unless the user has dismissed *this exact* message --
  // the actual message-vs-dismissedMessage comparison happens where the announcement is
  // fetched (Navbar), since this provider doesn't know the live content itself.
  const [showAnnouncement, setShowAnnouncementState] = useState<boolean>(true);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Check admin status on mount
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    setIsAdmin(!!adminToken);
  }, []);

  // Wrapper for setShowAnnouncement that also persists the dismissed message to localStorage
  const setShowAnnouncement = useCallback((show: boolean, message?: string) => {
    setShowAnnouncementState(show);
    if (!show && message) {
      localStorage.setItem('announcementDismissedMessage', message);
      setDismissedMessage(message);
    } else if (show) {
      localStorage.removeItem('announcementDismissedMessage');
      setDismissedMessage(null);
    }
  }, []);

  // Announcement is only visible when showAnnouncement is true AND user is not admin
  const isAnnouncementVisible = useMemo(() => {
    return showAnnouncement && !isAdmin;
  }, [showAnnouncement, isAdmin]);

  // Calculate header heights based on current state
  const headerHeight = useMemo((): HeaderHeight => {
    return {
      mobile: isAnnouncementVisible
        ? HEADER_CONSTANTS.ANNOUNCEMENT_HEIGHT + HEADER_CONSTANTS.NAVBAR_HEIGHT_MOBILE
        : HEADER_CONSTANTS.NAVBAR_HEIGHT_MOBILE,
      desktop: isAnnouncementVisible
        ? HEADER_CONSTANTS.ANNOUNCEMENT_HEIGHT + HEADER_CONSTANTS.NAVBAR_HEIGHT_DESKTOP
        : HEADER_CONSTANTS.NAVBAR_HEIGHT_DESKTOP,
    };
  }, [isAnnouncementVisible]);

  const value = useMemo<HeaderContextType>(() => ({
    showAnnouncement,
    setShowAnnouncement,
    dismissedMessage,
    isAdmin,
    setIsAdmin,
    headerHeight,
    isAnnouncementVisible,
  }), [showAnnouncement, setShowAnnouncement, dismissedMessage, isAdmin, headerHeight, isAnnouncementVisible]);

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
};

export default HeaderContext;


